<template>
  <div id="satelliteImagery">
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header satellite-header d-flex justify-content-between align-items-center">
              <div>
                <h4 class="mb-0"><i class="fas fa-satellite"></i> Satellite &amp; SAR Imagery Browser</h4>
                <p class="text-muted mb-0">
                  Browse and ingest NASA &amp; ESA open imagery for
                  <span v-if="currentFarm" class="font-weight-bold">{{ currentFarm.name }}</span>
                  <span v-else>your farm</span>
                </p>
              </div>
              <button @click="goBack()" class="btn btn-outline-secondary">
                <i class="fas fa-arrow-left"></i> Back to App
              </button>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4">
                  <!-- Block Selection Panel -->
                  <BlockSelectionPanel
                    :blocks="blocks"
                    :selectedBlock="selectedBlock"
                    :currentFarm="currentFarm"
                    :latestImagery="latestImagery"
                    :searchingImagery="searchingImagery"
                    :satelliteLayerAdded="satelliteLayerAdded"
                    @selectBlock="selectBlock"
                    @browseImagery="browseSatelliteImagery"
                    @downloadImagery="downloadImagery"
                    @saveImagery="saveImageryToAccount"
                    @saveAsDataset="saveCurrentSceneAsDataset"
                  />
                </div>
                
                <div class="col-md-8">
                  <!-- Map Display Panel -->
                  <MapDisplayPanel
                    :map="map"
                    :mapLoading="mapLoading"
                    :tileLoading="tileLoading"
                    :blocksGeoJson="blocksGeoJson"
                    :selectedBlock="selectedBlock"
                    :latestImagery="latestImagery"
                    :satelliteLayerAdded="satelliteLayerAdded"
                    @mapReady="onMapReady"
                    @blockSelected="onBlockSelected"
                  />
                </div>
              </div>
              <!-- NASA Acres attribution footer -->
              <div class="nasa-acres-footer mt-4">
                <div class="d-flex flex-wrap align-items-center justify-content-between">
                  <div class="small text-muted flex-grow-1 pr-3">
                    Imagery search powered by open STAC APIs —
                    <a href="https://earth-search.aws.element84.com/v1" target="_blank" rel="noopener">Element84 Earth Search</a> ·
                    <a href="https://cmr.earthdata.nasa.gov/stac/LPCLOUD" target="_blank" rel="noopener">NASA LP DAAC CMR-STAC</a>.
                    Data © ESA Copernicus / NASA / USGS.
                  </div>
                  <a
                    href="https://nasaacres.org"
                    target="_blank"
                    rel="noopener"
                    class="nasa-acres-badge"
                    title="Supported by NASA Acres"
                  >
                    <img
                      :src="nasaAcresLogo"
                      alt="NASA Acres"
                      class="nasa-acres-logo-img"
                    />
                    <span class="nasa-acres-caption">Supported by NASA Acres</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scene Selection Modal -->
    <SceneSelectionModal
      :show="showSceneModal"
      :scenes="availableScenes"
      :loading="loadingScenes"
      :catalog="collectionCatalog"
      :initialCollections="activeCollections"
      :emptyCollections="emptyCollections"
      @close="closeSceneModal"
      @select="selectScene"
      @preview="previewScene"
      @search="handleDateRangeSearch"
      @loadLatestForCollection="loadLatestForCollection"
    />

    <!-- Enhanced Dataset Creation Modal -->
    <DatasetPreviewModal
      :show="showDatasetModal"
      :scene="currentLoadedScene"
      :block="selectedBlock"
      @close="closeDatasetModal"
      @create="createMultipleDatasets"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import firebase from 'firebase/compat/app'
import 'firebase/compat/functions'
import BlockSelectionPanel from './components/BlockSelectionPanel.vue'
import MapDisplayPanel from './components/MapDisplayPanel.vue'
import SceneSelectionModal from './components/SceneSelectionModal.vue'
import DatasetPreviewModal from './components/DatasetPreviewModal.vue'
import SatelliteImageryService from './services/SatelliteImageryService.js'
import nasaAcresLogo from '@/assets/nasa-acres.png'
import MapService from './services/MapService.js'
import BlockService from './services/BlockService.js'

const fb = require('../../../firebaseConfig.js')

export default {
  name: 'SatelliteImagery',
  components: {
    BlockSelectionPanel,
    MapDisplayPanel,
    SceneSelectionModal,
    DatasetPreviewModal
  },
  data() {
    return {
      // Services
      satelliteService: null,
      mapService: null,
      blockService: null,
      
      // TiTiler Configuration
      titilerConfig: {
        baseUrl: 'https://titiler-231171307256.us-central1.run.app',
        tileMatrixSet: 'WebMercatorQuad',
        tileSize: 256
      },
      
      // Map state
      map: null,
      mapLoading: false,
      tileLoading: false,
      
      // Data
      blocks: [],
      selectedBlock: null,
      blocksGeoJson: null,
      highlightedBlockId: null,
      
      // Satellite imagery state
      latestImagery: null,
      satelliteLayerAdded: false,
      searchingImagery: false,
      showSceneModal: false,
      availableScenes: [],
      emptyCollections: [],
      nasaAcresLogo,
      collectionCatalog: [],
      activeCollections: [],
      selectedScene: null,
      currentLoadedScene: null,
      loadingScenes: false,
      
      // Enhanced dataset creation
      showDatasetModal: false,
      
      // Date range for imagery search
      dateRange: {
        startDate: null,
        endDate: null
      }
    }
  },
  computed: {
    ...mapState(['farm']),
    currentFarm() {
      return this.farm
    }
  },
  async mounted() {
    this.dateRange = this.getDefaultDateRange()
    await this.initializePlugin()
  },
  beforeDestroy() {
    if (this.map) {
      // Clear tile loading detection
      this.clearTileLoadingDetection()
      this.map.remove()
    }
  },
  methods: {
    /**
     * Per-collection MPC render defaults (asset picks + color formula / rescale).
     * Returns the query-string fragment to append to /item/tiles/ URLs.
     */
    buildMpcRenderParams(collectionId, scene) {
      const assets = Object.keys(scene?.assets || {})
      const has = n => assets.includes(n)
      const encode = v => encodeURIComponent(v)

      // Landsat Collection 2 L2 — scaled surface reflectance in red/green/blue.
      if (collectionId === 'landsat-c2-l2') {
        return 'assets=red&assets=green&assets=blue' +
          `&color_formula=${encode('gamma RGB 2.7, saturation 1.5, sigmoidal RGB 15 0.55')}`
      }
      // NASA HLS — 0–1 surface reflectance; B04/B03/B02.
      if (collectionId === 'hls2-l30' || collectionId === 'hls2-s30') {
        const r = has('B04') ? 'B04' : 'red'
        const g = has('B03') ? 'B03' : 'green'
        const b = has('B02') ? 'B02' : 'blue'
        return `assets=${r}&assets=${g}&assets=${b}` +
          `&rescale=0,3000&color_formula=${encode('gamma RGB 1.7, sigmoidal RGB 10 0.5')}`
      }
      // MODIS MOD13Q1 — vegetation indices. Default to NDVI with red-yellow-green ramp.
      if (collectionId === 'modis-13Q1-061') {
        const ndviKey = assets.find(a => /NDVI/i.test(a)) || '250m_16_days_NDVI'
        return `assets=${ndviKey}&rescale=-2000,10000&colormap_name=rdylgn`
      }
      // NAIP — pre-composed 4-band natural color; use first 3 bands as RGB.
      if (collectionId === 'naip') {
        return 'assets=image&asset_bidx=image%7C1,2,3'
      }
      // Impact Observatory / ESA WorldCover — categorical land cover.
      if (collectionId === 'io-lulc-9-class') {
        return 'assets=data&colormap_name=io-lulc-9-class'
      }
      if (collectionId === 'esa-worldcover') {
        return 'assets=map&colormap_name=esa-worldcover'
      }
      // USGS 3DEP DEM — terrain.
      if (collectionId === '3dep-seamless') {
        return 'assets=data&rescale=-500,4000&colormap_name=terrain'
      }
      // gNATSGO soils — default to plant-available water (0–100 cm), a farmer-friendly metric.
      // Falls back gracefully to whichever soil raster exists on the item.
      if (collectionId === 'gnatsgo-rasters') {
        const soilPreferred = ['aws0_100', 'aws0_150', 'aws0_50', 'aws0_25', 'soc0_30', 'om_r']
        const pick = soilPreferred.find(k => has(k)) || assets.find(a => !/mukey|hzdept/i.test(a)) || assets[0]
        // AWS is in mm (0–400 typical); SOC is % (0–10); generic 0,1 as last resort.
        const rescale = /aws/.test(pick) ? '0,400' : /soc|om_r/.test(pick) ? '0,10' : '0,1'
        const cmap = /aws/.test(pick) ? 'blues' : /soc|om_r/.test(pick) ? 'ylorbr' : 'viridis'
        return `assets=${pick}&rescale=${rescale}&colormap_name=${cmap}`
      }
      // Daymet daily climate — default to max temperature (°C).
      if (collectionId === 'daymet-daily-na') {
        const climatePreferred = ['tmax', 'tmin', 'prcp', 'srad', 'vp', 'swe']
        const pick = climatePreferred.find(k => has(k)) || assets[0]
        const rescale = /tmax|tmin/.test(pick) ? '-30,40'
          : /prcp/.test(pick) ? '0,50'
          : /srad/.test(pick) ? '0,1000'
          : '0,1'
        const cmap = /tmax|tmin/.test(pick) ? 'rdbu_r' : /prcp/.test(pick) ? 'blues' : 'viridis'
        return `assets=${pick}&rescale=${rescale}&colormap_name=${cmap}`
      }
      // Generic RGB fallback if the scene exposes red/green/blue.
      if (has('red') && has('green') && has('blue')) {
        return 'assets=red&assets=green&assets=blue' +
          `&color_formula=${encode('gamma RGB 2.5, saturation 1.4, sigmoidal RGB 12 0.5')}`
      }
      // Last resort: single-band grayscale of the first asset.
      return `assets=${assets[0] || 'data'}`
    },

    getDefaultDateRange() {
      const endDate = new Date()
      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
      return { startDate, endDate }
    },
    
    async initializePlugin() {
      console.log('🛰️ Initializing Satellite Imagery plugin...')
      
      // Check if we have a farm loaded
      if (!this.currentFarm || !this.currentFarm.id) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'No farm selected. Please select a farm first.',
          level: 'alert-warning',
          timeout: 5000
        })
        return
      }

      // Initialize services
      this.satelliteService = new SatelliteImageryService()
      this.mapService = new MapService()
      this.blockService = new BlockService()

      // Populate collection catalog for the scene-selection modal
      this.collectionCatalog = this.satelliteService.getCollectionCatalog()
      this.activeCollections = [...this.satelliteService.defaultCollections]

      this.mapLoading = true
      
      try {
        // Load blocks data
        await this.loadBlocks()
        
        // Initialize map after blocks are loaded
        this.$nextTick(() => {
          this.initializeMap()
        })
      } catch (error) {
        console.error('Error initializing plugin:', error)
        this.$store.commit('setAlert', {
          active: true,
          message: 'Error loading farm data. Please try again.',
          level: 'alert-danger',
          timeout: 5000
        })
        this.mapLoading = false
      }
    },

    async loadBlocks() {
      console.log('Loading blocks for farm:', this.currentFarm.id)
      
      try {
        // Load blocks from Firestore
        const blocksSnapshot = await fb.blocksCollection
          .where('farmId', '==', this.currentFarm.id)
          .get()
        
        this.blocks = []
        blocksSnapshot.forEach(doc => {
          const block = doc.data()
          block.id = doc.id
          this.blocks.push(block)
        })
        
        console.log('Loaded', this.blocks.length, 'blocks')
        
        if (this.blocks.length === 0) {
          this.$store.commit('setAlert', {
            active: true,
            message: 'No blocks found for this farm. Please create some blocks first.',
            level: 'alert-warning',
            timeout: 5000
          })
        }
        
        // Load blocks GeoJSON if available
        if (this.currentFarm.blocksGeoJsonPath) {
          await this.loadBlocksGeoJson()
        } else {
          console.log('No blocks GeoJSON path available')
          this.$store.commit('setAlert', {
            active: true,
            message: 'Block geometry data not available. Map visualization may be limited.',
            level: 'alert-info',
            timeout: 3000
          })
        }
      } catch (error) {
        console.error('Error loading blocks:', error)
        this.$store.commit('setAlert', {
          active: true,
          message: 'Error loading blocks. Please try again.',
          level: 'alert-danger',
          timeout: 5000
        })
        throw error
      }
    },

    async loadBlocksGeoJson() {
      try {
        const blocksGeoJsonStorageRef = firebase.storage().ref(this.currentFarm.blocksGeoJsonPath)
        const blocksGeoJsonUrl = await blocksGeoJsonStorageRef.getDownloadURL()
        const response = await fetch(blocksGeoJsonUrl)
        this.blocksGeoJson = await response.json()
        console.log('Loaded blocks GeoJSON with', this.blocksGeoJson.features.length, 'features')
      } catch (error) {
        console.error('Error loading blocks GeoJSON:', error)
        // Don't throw error here, we can still work without GeoJSON
      }
    },

    initializeMap() {
      console.log('Initializing map...')
      
      // Check if the container element exists
      const container = document.getElementById('satelliteImageryMap')
      if (!container) {
        console.log('Map container not found. Retrying in 200ms...')
        setTimeout(() => {
          this.initializeMap()
        }, 200)
        return
      }

      try {
        // Set Mapbox access token
        // eslint-disable-next-line no-undef
        mapboxgl.accessToken = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ'
        
        // Initialize Mapbox map
        // eslint-disable-next-line no-undef
        this.map = new mapboxgl.Map({
          container: 'satelliteImageryMap',
          style: 'mapbox://styles/mapbox/satellite-v9',
          center: this.getMapCenter(),
          zoom: 15
        })

        this.map.on('load', () => {
          console.log('Map loaded, adding blocks layer...')
          this.addBlocksLayer()
          this.mapLoading = false
        })

        this.map.on('error', (error) => {
          console.error('Map error:', error)
          this.mapLoading = false
          this.$store.commit('setAlert', {
            active: true,
            message: 'Error loading map. Please refresh the page and try again.',
            level: 'alert-danger',
            timeout: 5000
          })
        })

      } catch (error) {
        console.error('Error initializing map:', error)
        this.mapLoading = false
      }
    },

    getMapCenter() {
      // Use farm location if available, otherwise default
      if (this.currentFarm.geolocation) {
        return [
          this.currentFarm.geolocation.longitude,
          this.currentFarm.geolocation.latitude
        ]
      }
      return [-79.5, 42.4] // Default center
    },

    addBlocksLayer() {
      if (!this.blocksGeoJson || !this.blocksGeoJson.features.length) {
        console.log('No blocks GeoJSON available')
        return
      }

      // Add blocks source
      this.map.addSource('blocks-source', {
        type: 'geojson',
        data: this.blocksGeoJson
      })

      // Add blocks outline layer (no fill)
      this.map.addLayer({
        id: 'blocks-outline',
        type: 'line',
        source: 'blocks-source',
        paint: {
          'line-width': this.currentFarm.vizSettings?.lineWidth || 2,
          'line-color': this.currentFarm.vizSettings?.lineColor || '#6e59c7'
        }
      })

      // Add block labels
      this.map.addLayer({
        id: 'blocks-labels',
        type: 'symbol',
        source: 'blocks-source',
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 12,
          'text-anchor': 'center',
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2
        }
      })

      // Add click handler
      this.map.on('click', 'blocks-outline', (e) => {
        if (e.features.length > 0) {
          const feature = e.features[0]
          const blockId = feature.properties.blockId
          this.selectBlockById(blockId)
        }
      })

      // Add hover effects
      this.map.on('mouseenter', 'blocks-outline', () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })

      this.map.on('mouseleave', 'blocks-outline', () => {
        this.map.getCanvas().style.cursor = ''
      })

      // Fit map to blocks bounds
      this.fitMapToBlocks()
    },

    fitMapToBlocks() {
      if (!this.blocksGeoJson || !this.blocksGeoJson.features.length) {
        return
      }

      try {
        // Calculate bounds
        // eslint-disable-next-line no-undef
        const bounds = new mapboxgl.LngLatBounds()
        
        this.blocksGeoJson.features.forEach(feature => {
          const coordinates = feature.geometry.coordinates
          if (feature.geometry.type === 'Polygon') {
            coordinates[0].forEach(coord => {
              if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                bounds.extend(coord)
              }
            })
          }
        })

        this.map.fitBounds(bounds, {
          padding: 50,
          animate: false
        })
      } catch (error) {
        console.error('Error fitting map to blocks:', error)
      }
    },

    async selectBlock(block) {
      console.log('Selecting block:', block)
      this.selectedBlock = block
      this.highlightBlockOnMap(block.id)
      
      // Reset date range to default when changing blocks
      this.dateRange = this.getDefaultDateRange()
      
      // Clear previous satellite imagery when selecting a new block
      this.latestImagery = null
      await this.clearSatelliteLayers()
    },

    async clearSatelliteLayers() {
      console.log('🧹 Clearing satellite layers...')
      
      // Clear tile loading detection
      this.clearTileLoadingDetection()
      
      if (this.map) {
        // Remove satellite layer if it exists
        if (this.map.getLayer('earth-search-scene-layer')) {
          this.map.removeLayer('earth-search-scene-layer')
          console.log('✅ Removed earth-search-scene-layer')
        }
        
        // Remove satellite source if it exists
        if (this.map.getSource('earth-search-scene')) {
          this.map.removeSource('earth-search-scene')
          console.log('✅ Removed earth-search-scene source')
        }
        
        // Remove scene info layer if it exists
        if (this.map.getLayer('scene-info')) {
          this.map.removeLayer('scene-info')
          console.log('✅ Removed scene-info layer')
        }
        
        // Remove scene info source if it exists
        if (this.map.getSource('scene-info')) {
          this.map.removeSource('scene-info')
          console.log('✅ Removed scene-info source')
        }
        
        // Force map refresh
        this.map.triggerRepaint()
        
        // Wait for map to be idle
        await new Promise(resolve => {
          const checkIdle = () => {
            if (this.map.loaded()) {
              resolve()
            } else {
              setTimeout(checkIdle, 100)
            }
          }
          checkIdle()
        })
        
        console.log('🧹 Map idle after clearing layers/sources.')
      }
      
      this.satelliteLayerAdded = false
    },

    selectBlockById(blockId) {
      const block = this.blocks.find(b => b.id === blockId)
      if (block) {
        this.selectBlock(block)
      }
    },

    highlightBlockOnMap(blockId) {
      // Remove previous highlight
      if (this.map.getLayer('highlighted-block')) {
        this.map.removeLayer('highlighted-block')
      }
      if (this.map.getSource('highlighted-block-source')) {
        this.map.removeSource('highlighted-block-source')
      }

      // Find the block feature
      const blockFeature = this.blocksGeoJson.features.find(f => f.properties.blockId === blockId)
      if (blockFeature) {
        // Add highlight source
        this.map.addSource('highlighted-block-source', {
          type: 'geojson',
          data: blockFeature
        })

        // Add highlight layer (thick outline)
        this.map.addLayer({
          id: 'highlighted-block',
          type: 'line',
          source: 'highlighted-block-source',
          paint: {
            'line-color': '#ffc107',
            'line-width': 4
          }
        })

        // Fit map to highlighted block
        // eslint-disable-next-line no-undef
        const bounds = new mapboxgl.LngLatBounds()
        const coordinates = blockFeature.geometry.coordinates[0]
        coordinates.forEach(coord => {
          bounds.extend(coord)
        })
        
        this.map.fitBounds(bounds, {
          padding: 20,
          duration: 1000
        })
      }
    },

    async handleDateRangeSearch(payload) {
      console.log('🔍 Search requested:', payload)
      this.dateRange = { startDate: payload.startDate, endDate: payload.endDate }
      if (Array.isArray(payload.collections)) {
        this.activeCollections = payload.collections
      }
      await this.performSatelliteSearch()
    },

    /**
     * Archive-wide "load latest" fallback for a single collection that
     * returned 0 results within the active date range.
     */
    async loadLatestForCollection(collectionId) {
      if (!this.selectedBlock) return
      const blockFeature = this.blocksGeoJson.features.find(f => f.properties.blockId === this.selectedBlock.id)
      if (!blockFeature) return
      this.loadingScenes = true
      try {
        console.log(`🔎 Loading latest ${collectionId} (archive-wide)…`)
        const resp = await this.satelliteService.searchLatestForCollection(collectionId, blockFeature.geometry, 8)
        const added = resp?.features || []
        if (added.length === 0) {
          this.$store.commit('setAlert', {
            active: true,
            message: `No ${collectionId} scenes found for this block in the entire archive.`,
            level: 'alert-warning',
            timeout: 5000
          })
          return
        }
        // Pin archive-loaded scenes to the top so the user sees what they just asked for.
        // Tag them so the modal can show a subtle "out-of-range" badge.
        const pinned = added.map(s => ({ ...s, __loadedLatest: true }))
        const existing = new Set(pinned.map(s => s.id))
        const rest = this.availableScenes.filter(s => !existing.has(s.id))
        this.availableScenes = [...pinned, ...rest]
        this.emptyCollections = this.emptyCollections.filter(c => c !== collectionId)
        this.$store.commit('setAlert', {
          active: true,
          message: `✅ Loaded ${added.length} latest ${collectionId} scene(s).`,
          level: 'alert-success',
          timeout: 4000
        })
      } catch (err) {
        console.error('❌ Load latest failed:', err)
        this.$store.commit('setAlert', {
          active: true,
          message: `Error loading latest ${collectionId} scenes.`,
          level: 'alert-danger',
          timeout: 5000
        })
      } finally {
        this.loadingScenes = false
      }
    },
    
    async browseSatelliteImagery() {
      if (!this.selectedBlock) return
      
      this.loadingScenes = true
      this.showSceneModal = true
      
      await this.performSatelliteSearch()
    },
    
    async performSatelliteSearch() {
      if (!this.selectedBlock) return
      
      this.loadingScenes = true
      
      try {
        // Get the block geometry from GeoJSON
        const blockFeature = this.blocksGeoJson.features.find(f => f.properties.blockId === this.selectedBlock.id)
        if (!blockFeature) {
          throw new Error('Block geometry not found')
        }
        
        console.log('🛰️ Searching Earth Search for scenes...')
        console.log('📅 Date range:', this.dateRange)
        
        // Search for satellite imagery using Earth Search with date range
        const imagery = await this.satelliteService.searchLatestImagery(
          blockFeature.geometry,
          20,
          this.dateRange,
          this.activeCollections && this.activeCollections.length ? this.activeCollections : null
        )

        console.log('🛰️ Imagery:', imagery)
        
        this.emptyCollections = imagery?.emptyCollections || []
        if (imagery && imagery.features && imagery.features.length > 0) {
          this.availableScenes = imagery.features
          console.log(`✅ Found ${imagery.features.length} scenes; empty:`, this.emptyCollections)
        } else {
          this.availableScenes = []
          this.$store.commit('setAlert', {
            active: true,
            message: 'No satellite imagery found for this block in the selected date range. Try expanding the date range.',
            level: 'alert-warning',
            timeout: 5000
          })
        }
      } catch (error) {
        console.error('❌ Error searching satellite imagery:', error)
        this.availableScenes = []
        this.$store.commit('setAlert', {
          active: true,
          message: 'Error searching satellite imagery. Please try again.',
          level: 'alert-danger',
          timeout: 5000
        })
      } finally {
        this.loadingScenes = false
      }
    },

    async addSatellitePreviewLayer(sceneId) {
      console.log('Adding satellite preview for scene ID:', sceneId)

      // Use the real cloud function response data
      try {
        // Check if we have real data from the cloud function
        if (this.cloudFunctionResponse && this.cloudFunctionResponse.metadata) {
          const metadata = this.cloudFunctionResponse.metadata;
          const downloadInfo = metadata.downloadInfo;
          
          console.log('Using real cloud function data:', {
            sceneId: metadata.sceneId,
            sceneTitle: metadata.sceneTitle,
            cloudCover: metadata.cloudCover,
            downloadUrl: downloadInfo?.subsetUrl
          });
          
          // Add the satellite imagery layer if we have an image URL
          if (this.cloudFunctionResponse.imageUrl) {
            console.log('Adding satellite imagery layer:', this.cloudFunctionResponse.imageUrl);
            
            try {
              // Add the satellite image layer
              this.map.addSource('satellite-preview', {
                type: 'image',
                url: this.cloudFunctionResponse.imageUrl,
                coordinates: this.cloudFunctionResponse.coordinates
              });
              
              this.map.addLayer({
                id: 'satellite-preview',
                type: 'raster',
                source: 'satellite-preview',
                paint: { 
                  'raster-opacity': 0.9,
                  'raster-resampling': 'linear'
                }
              }, 'blocks-outline');
              
              console.log('✅ Satellite imagery layer added successfully');
              
              // Listen for image loading errors
              this.map.on('error', (e) => {
                if (e.error && e.error.message && e.error.message.includes('image')) {
                  console.log('Image loading failed, using fallback approach');
                  // Remove the failed image layer
                  if (this.map.getLayer('satellite-preview')) {
                    this.map.removeLayer('satellite-preview');
                  }
                  if (this.map.getSource('satellite-preview')) {
                    this.map.removeSource('satellite-preview');
                  }
                }
              });
              
            } catch (imageError) {
              console.error('Error adding satellite imagery layer:', imageError);
              // Continue without the image layer
            }
          }
          
          // Add the block highlight
          this.map.addSource('block-highlight', {
            type: 'geojson',
            data: this.blocksGeoJson.features.find(f => f.properties.blockId === this.selectedBlock.id)
          })
          
          this.map.addLayer({
            id: 'block-highlight',
            type: 'line',
            source: 'block-highlight',
            paint: { 
              'line-color': '#ff0000',
              'line-width': 3,
              'line-opacity': 0.8
            }
          })
          
          // Add scene info overlay
          this.addSceneInfoOverlay(sceneId)
          
          this.satelliteLayerAdded = true
          
          console.log('Satellite preview layer added successfully with real data')
          
          // Show success message with real data
          const sceneTitle = metadata.sceneTitle || sceneId;
          const cloudCover = metadata.cloudCover || 'Unknown';
          const platform = metadata.sceneProperties?.platform || 'Sentinel-2';
          const acquisitionDate = metadata.acquisitionDate ? new Date(metadata.acquisitionDate).toLocaleDateString() : 'Unknown';
          
          this.$store.commit('setAlert', {
            active: true,
            message: `✅ Real satellite imagery found! ${platform} scene: ${sceneTitle.substring(0, 30)}... Cloud cover: ${cloudCover}% | Date: ${acquisitionDate}`,
            level: 'alert-success',
            timeout: 10000
          })
          
          // If we have download info, show it
          if (downloadInfo) {
            console.log('Download information available:', {
              originalSize: downloadInfo.originalSizeMB + 'MB',
              subsetUrl: downloadInfo.subsetUrl,
              bands: downloadInfo.bands
            });
          }
          
        } else {
          // Fallback to basic block highlight
          this.map.addSource('block-highlight', {
            type: 'geojson',
            data: this.blocksGeoJson.features.find(f => f.properties.blockId === this.selectedBlock.id)
          })
          
          this.map.addLayer({
            id: 'block-highlight',
            type: 'line',
            source: 'block-highlight',
            paint: { 
              'line-color': '#ff0000',
              'line-width': 3,
              'line-opacity': 0.8
            }
          })
          
          this.satelliteLayerAdded = true
          
          this.$store.commit('setAlert', {
            active: true,
            message: `Found satellite scene ${sceneId.substring(0, 20)}... but no real data available.`,
            level: 'alert-warning',
            timeout: 8000
          })
        }
        
      } catch (error) {
        console.error('Error adding satellite preview layer:', error)
        
        this.$store.commit('setAlert', {
          active: true,
          message: `Error displaying satellite data: ${error.message}`,
          level: 'alert-danger',
          timeout: 8000
        })
      }
    },

    addSceneInfoOverlay(sceneId, imageryUrl = null, metadata = null) {
      if (!this.latestImagery || !this.selectedBlock) return
      
      // Safely get block coordinates
      let blockCenter = [0, 0]
      try {
        if (this.selectedBlock.geometry && this.selectedBlock.geometry.coordinates && 
            this.selectedBlock.geometry.coordinates[0] && this.selectedBlock.geometry.coordinates[0].length >= 3) {
          const coords = this.selectedBlock.geometry.coordinates[0]
          blockCenter = [
            (coords[0][0] + coords[2][0]) / 2,
            (coords[0][1] + coords[2][1]) / 2
          ]
        } else if (this.blocksGeoJson && this.blocksGeoJson.features) {
          const blockFeature = this.blocksGeoJson.features.find(f => f.properties.blockId === this.selectedBlock.id)
          if (blockFeature && blockFeature.geometry && blockFeature.geometry.coordinates) {
            const coords = blockFeature.geometry.coordinates[0]
            blockCenter = [
              (coords[0][0] + coords[2][0]) / 2,
              (coords[0][1] + coords[2][1]) / 2
            ]
          }
        }
      } catch (error) {
        console.log('Error calculating block center, using default coordinates:', error)
      }
      
      // Add scene information as a map overlay
      const sceneInfo = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: blockCenter
          },
          properties: {
            title: metadata?.title || (sceneId.includes('S2A_') 
              ? 'Sentinel-2 Scene Found' 
              : 'Satellite Scene Found'),
            sceneId: sceneId,
            datetime: this.formatDate(this.latestImagery.datetime),
            cloudCover: this.latestImagery.cloudCover ? `${this.latestImagery.cloudCover}%` : 'Unknown',
            collection: this.latestImagery.collection || 'Unknown',
            imageryUrl: imageryUrl,
            assetType: metadata?.assetType || 'Unknown',
            note: metadata?.note || null
          }
        }]
      }
      
      this.map.addSource('scene-info', {
        type: 'geojson',
        data: sceneInfo
      })
      
      this.map.addLayer({
        id: 'scene-info',
        type: 'symbol',
        source: 'scene-info',
        layout: {
          'text-field': ['concat', 
            '📡 ', ['get', 'title'], '\n', 
            ['get', 'datetime'], '\n',
            'Cloud Cover: ', ['get', 'cloudCover']
          ],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 2
        }
      })
    },
    
    async downloadImagery() {
      this.$store.commit('setAlert', {
        active: true,
        message: `To download the full Sentinel-2 scene, visit Copernicus Open Access Hub and search for scene ID: ${this.latestImagery.id}. The scene file is typically 100-500MB and contains all spectral bands.`,
        level: 'alert-info',
        timeout: 10000
      })
    },

    async saveImageryToAccount() {
      if (!this.cloudFunctionResponse || !this.cloudFunctionResponse.metadata) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'No satellite imagery data available to save',
          level: 'alert-warning',
          timeout: 5000
        });
        return;
      }

      try {
        console.log('Saving satellite imagery to user account...');
        
        const metadata = this.cloudFunctionResponse.metadata;
        const saveData = {
          userId: this.$store.state.user?.uid || 'anonymous',
          farmId: this.currentFarm?.id,
          blockId: this.selectedBlock.id,
          sceneId: metadata.sceneId,
          sceneTitle: metadata.sceneTitle,
          acquisitionDate: metadata.acquisitionDate,
          cloudCover: metadata.cloudCover,
          platform: metadata.sceneProperties?.platform,
          resolution: metadata.resolution,
          bands: metadata.bands,
          downloadInfo: metadata.downloadInfo,
          coordinates: this.cloudFunctionResponse.coordinates,
          imageUrl: this.cloudFunctionResponse.imageUrl,
          savedAt: new Date().toISOString()
        };

        // Save to Firestore
        const db = this.$firebase.firestore();
        const savedImageryRef = await db.collection('savedSatelliteImagery').add(saveData);
        
        console.log('✅ Satellite imagery saved to account:', savedImageryRef.id);
        
        this.$store.commit('setAlert', {
          active: true,
          message: `✅ Satellite imagery saved to your account! Scene: ${metadata.sceneTitle.substring(0, 30)}...`,
          level: 'alert-success',
          timeout: 8000
        });
        
      } catch (error) {
        console.error('Error saving satellite imagery:', error);
        
        this.$store.commit('setAlert', {
          active: true,
          message: `Error saving satellite imagery: ${error.message}`,
          level: 'alert-danger',
          timeout: 8000
        });
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    goBack() {
      // Navigate back to the farm page
      const farmId = this.currentFarm?.id
      if (farmId) {
        this.$router.push({ path: `/f/${farmId}` })
      } else {
        this.$router.push({ path: '/f' })
      }
    },

    // Event handlers for child components
    onMapReady(map) {
      this.map = map
    },

    onBlockSelected(block) {
      this.selectBlock(block)
    },

    closeSceneModal() {
      this.showSceneModal = false
      this.availableScenes = []
      this.selectedScene = null
    },

    async selectScene(scene) {
      console.log('🛰️ Selected scene:', scene)
      
      // Prevent duplicate processing
      if (this.selectedScene && this.selectedScene.id === scene.id && this.satelliteLayerAdded) {
        console.log('🔄 Scene already loaded, skipping...')
        return
      }
      
      this.selectedScene = scene
      this.latestImagery = this.satelliteService.processImageryResult(scene)
      
      // Show loading state
      this.$store.commit('setAlert', {
        active: true,
        message: '🔄 Loading satellite imagery...',
        level: 'alert-info',
        timeout: 2000
      })
      
      try {
        console.log('🔄 About to call addEarthSearchSceneToMap...')
        
        // Show tile loading spinner
        this.tileLoading = true
        
        // Add the scene to the map
        await this.addEarthSearchSceneToMap(scene)
        console.log('✅ addEarthSearchSceneToMap completed successfully')
        
        // Update the current loaded scene
        this.currentLoadedScene = scene

        // The SAR branch inside addEarthSearchSceneToMap already shows a
        // tailored alert; don't overwrite it.
        const sarSelected = (scene.kind || scene.collectionMeta?.kind) === 'sar' ||
          String(scene.collection || '').startsWith('sentinel-1')
        if (!sarSelected) {
          this.$store.commit('setAlert', {
            active: true,
            message: `✅ Selected ${scene.source} scene from ${this.formatDate(scene.properties.datetime)}`,
            level: 'alert-success',
            timeout: 3000
          })
        }
      } catch (error) {
        console.error('❌ Error selecting scene:', error)
        
        this.$store.commit('setAlert', {
          active: true,
          message: `❌ Error loading imagery: ${error.message}`,
          level: 'alert-danger',
          timeout: 5000
        })
        
        // Hide tile loading spinner on error
        this.tileLoading = false
      }
      // Note: tileLoading is now managed by tile loading detection in addEarthSearchSceneToMap
    },

    previewScene(scene) {
      console.log('👁️ Previewing scene:', scene)
      // For now, just select the scene
      this.selectScene(scene)
    },

    async addEarthSearchSceneToMap(scene) {
      console.log('🚀 addEarthSearchSceneToMap called with scene:', scene.id)

      if (!this.map || !scene.assets) {
        console.log('❌ Early return - map or assets not available')
        return
      }

      console.log('✅ Map and assets available, proceeding...')

      try {
        console.log('🔄 About to clear satellite layers...')
        // Clear previous satellite layers
        await this.clearSatelliteLayers()
        console.log('✅ Satellite layers cleared successfully')

        // --- SAR short-circuit (legacy Sentinel-1 GRD only) -------------------
        // Earth Search's sentinel-1-grd exposes assets as s3://sentinel-s1-l1c/…
        // (requester-pays, non-COG SAFE TIFFs) which public TiTiler can't render.
        // Sentinel-1 RTC via Planetary Computer IS previewable and falls through
        // to the normal COG path below.
        const kind = scene.kind || scene.collectionMeta?.kind ||
          (String(scene.collection || '').startsWith('sentinel-1') ? 'sar' : 'optical')
        const previewable = scene.collectionMeta?.previewable !== false
        if (kind === 'sar' && !previewable) {
          console.log('📡 Legacy SAR scene selected — skipping browser tile preview (server-side ingest only).')
          this.tileLoading = false
          this.satelliteLayerAdded = true
          this.$store.commit('setAlert', {
            active: true,
            message:
              '📡 Sentinel-1 GRD selected. Browser preview unavailable (requester-pays archive); ' +
              'use "Save as Dataset" to process VV / VH / RVI server-side, ' +
              'or pick a Sentinel-1 RTC scene for live preview.',
            level: 'alert-info',
            timeout: 8000
          })
          return
        }
        
        // Pick a previewable asset. For SAR we preview VH (volume scattering,
        // most informative channel for canopy) via a dB expression + greys ramp.
        // For optical MPC scenes without a pre-composed `visual` asset (Landsat,
        // HLS, MODIS), we route through MPC's Data API tile endpoint which
        // builds RGB from red/green/blue and auto-signs internally.
        let visualAsset = null
        let sarExpression = null
        let mpcTileUrl = null
        const collectionId = scene.collection || scene.collectionMeta?.id
        const isMpcScene = scene.collectionMeta?.endpoint === 'planetaryComputer' ||
          Object.values(scene.assets || {}).some(a => typeof a?.href === 'string' && /blob\.core\.windows\.net/i.test(a.href))

        if (kind === 'sar') {
          visualAsset = scene.assets.vh || scene.assets.vv
          sarExpression = '10*log10(b1)'
        } else if (scene.assets.visual) {
          visualAsset = scene.assets.visual
        } else if (isMpcScene && collectionId) {
          // MPC Data API tile endpoint — RGB composite with per-collection default rendering.
          // We send `assets=red&assets=green&assets=blue` plus a gentle color formula.
          const renderParams = this.buildMpcRenderParams(collectionId, scene)
          mpcTileUrl = 'https://planetarycomputer.microsoft.com/api/data/v1/item/tiles/' +
            'WebMercatorQuad/{z}/{x}/{y}@1x?' +
            `collection=${encodeURIComponent(collectionId)}` +
            `&item=${encodeURIComponent(scene.id)}` +
            `&${renderParams}`
          console.log('🛰️ Using MPC Data API tile endpoint:', mpcTileUrl)
        } else {
          visualAsset = scene.assets.B04 || scene.assets.B02 || scene.assets.red
        }
        
        console.log('🔍 Available Sentinel-2 assets:', Object.keys(scene.assets))
        console.log('🎯 Selected visual asset:', visualAsset)
        console.log('🔍 Visual asset href:', visualAsset?.href)
        console.log('🔍 Visual asset exists and has href:', !!(visualAsset && visualAsset.href))
        
        if (mpcTileUrl || (visualAsset && visualAsset.href)) {
          if (mpcTileUrl) {
            console.log('🖼️ Adding MPC scene to map via Data API tiles:', mpcTileUrl)
          } else {
            console.log('🖼️ Adding Earth Search scene to map:', visualAsset.href)
            console.log('📊 Asset details:', {
              title: visualAsset.title,
              description: visualAsset.description,
              roles: visualAsset.roles,
              type: visualAsset.type
            })
          }
          
          // Use TiTiler for Sentinel-2 imagery
          console.log('🛰️ Using Sentinel-2 TiTiler approach')
          
          // Build tile URL — prefer MPC Data API for multi-band optical scenes
          // (Landsat, HLS, MODIS) that lack a pre-composed `visual` asset.
          let titilerUrl
          if (mpcTileUrl) {
            titilerUrl = mpcTileUrl
          } else {
            const cogUrl = visualAsset.href
            const sarParams = sarExpression
              ? `&expression=${encodeURIComponent(sarExpression)}&rescale=-25,0&colormap_name=greys`
              : ''
            titilerUrl = `${this.titilerConfig.baseUrl}/cog/tiles/${this.titilerConfig.tileMatrixSet}/{z}/{x}/{y}.png?url=${encodeURIComponent(cogUrl)}${sarParams}`
          }
          
          console.log('🔗 TiTiler URL:', titilerUrl)
          
          // Check if source already exists and remove it
          if (this.map.getSource('earth-search-scene')) {
            console.log('🔄 Removing existing earth-search-scene source')
            this.map.removeLayer('earth-search-scene-layer')
            this.map.removeSource('earth-search-scene')
          }
          
          // Add the satellite imagery layer using TiTiler
          this.map.addSource('earth-search-scene', {
            type: 'raster',
            tiles: [titilerUrl],
            tileSize: this.titilerConfig.tileSize
          })
          
          this.map.addLayer({
            id: 'earth-search-scene-layer',
            type: 'raster',
            source: 'earth-search-scene',
            paint: {
              'raster-opacity': 0.9
            }
          }, this.map.getLayer('highlighted-block') ? 'highlighted-block' : 'blocks-outline')
          
          // Set up tile loading detection
          this.setupTileLoadingDetection()
          
          // Force map refresh and tile loading
          this.map.triggerRepaint()
          
          // Force tiles to load for current viewport
          setTimeout(() => {
            if (this.map.getSource('earth-search-scene')) {
              // For raster sources, we can't use reload(), but triggerRepaint() should be sufficient
              this.map.triggerRepaint()
            }
          }, 100)
          
          // Listen for tile loading errors
          this.map.on('error', (e) => {
            if (e.error && e.error.message && e.error.message.includes('tile')) {
              console.warn('⚠️ Tile loading error:', e.error.message)
              // Don't show error to user, just log it
            }
          })
          
          this.satelliteLayerAdded = true
          
          console.log('✅ Sentinel-2 scene added to map using TiTiler')
          
          // Show success message
          this.$store.commit('setAlert', {
            active: true,
            message: `✅ Sentinel-2 imagery loaded! Scene from ${this.formatDate(scene.properties.datetime)}`,
            level: 'alert-success',
            timeout: 5000
          })
          
          // Add scene info overlay for additional details
          this.addSceneInfoOverlay(scene.id, (visualAsset && visualAsset.href) || mpcTileUrl || '')
          
        } else {
          console.log('⚠️ No visual asset found for scene')
          console.log('🔍 All available assets:', Object.keys(scene.assets))
          console.log('📋 Asset details:', scene.assets)
          
          // Log details of Sentinel-2 assets
          const keyAssets = ['visual', 'B04', 'B02', 'B03', 'B08']
          keyAssets.forEach(key => {
            if (scene.assets[key]) {
              console.log(`📊 ${key} asset:`, {
                href: scene.assets[key].href,
                type: scene.assets[key].type,
                title: scene.assets[key].title,
                roles: scene.assets[key].roles
              })
            }
          })
          
          // Try to find any COG asset that might work. Require an https:// URL
          // — s3:// hrefs (e.g. Sentinel-1 GRD on the requester-pays
          // sentinel-s1-l1c bucket) cannot be fetched by TiTiler anonymously.
          const cogAssets = Object.entries(scene.assets).filter(([key, asset]) => {
            const href = asset.href || ''
            if (!href.startsWith('http')) return false
            return (
              href.includes('.tif') ||
              href.includes('.cog') ||
              asset.type === 'image/tiff; application=geotiff' ||
              asset.type === 'image/tiff'
            )
          })
          
          if (cogAssets.length > 0) {
            console.log('🎯 Found COG assets:', cogAssets.map(([key, asset]) => ({ key, href: asset.href })))
            const [firstKey, firstAsset] = cogAssets[0]
            console.log(`🔄 Trying first COG asset: ${firstKey}`)
            
            // Use the first available COG asset
            const cogUrl = firstAsset.href
            const titilerUrl = `${this.titilerConfig.baseUrl}/cog/tiles/${this.titilerConfig.tileMatrixSet}/{z}/{x}/{y}.png?url=${encodeURIComponent(cogUrl)}`
            
            // Check if source already exists and remove it
            if (this.map.getSource('earth-search-scene')) {
              console.log('🔄 Removing existing earth-search-scene source')
              this.map.removeLayer('earth-search-scene-layer')
              this.map.removeSource('earth-search-scene')
            }
            
            // Add the satellite imagery layer using TiTiler
            this.map.addSource('earth-search-scene', {
              type: 'raster',
              tiles: [titilerUrl],
              tileSize: this.titilerConfig.tileSize
            })
            
            this.map.addLayer({
              id: 'earth-search-scene-layer',
              type: 'raster',
              source: 'earth-search-scene',
              paint: {
                'raster-opacity': 0.9
              }
            }, this.map.getLayer('highlighted-block') ? 'highlighted-block' : 'blocks-outline')
            
            // Set up tile loading detection
            this.setupTileLoadingDetection()
            
            // Force map refresh and tile loading
            this.map.triggerRepaint()
            
            // Force tiles to load for current viewport
            setTimeout(() => {
              if (this.map.getSource('earth-search-scene')) {
                // For raster sources, we can't use reload(), but triggerRepaint() should be sufficient
                this.map.triggerRepaint()
              }
            }, 100)
            
            this.satelliteLayerAdded = true
            
            console.log('✅ Earth Search scene added to map using fallback COG asset')
            
            this.$store.commit('setAlert', {
              active: true,
              message: `✅ Satellite imagery loaded! ${scene.source} scene from ${this.formatDate(scene.properties.datetime)} (using ${firstKey})`,
              level: 'alert-success',
              timeout: 5000
            })
            
            // Add scene info overlay for additional details
            this.addSceneInfoOverlay(scene.id, firstAsset.href)
          } else {
            // Fallback: show scene info overlay
            this.addSceneInfoOverlay(scene.id)
            
            this.$store.commit('setAlert', {
              active: true,
              message: `Scene found but no visual asset available. Scene ID: ${scene.id}`,
              level: 'alert-warning',
              timeout: 5000
            })
          }
        }
      } catch (error) {
        console.error('❌ Error adding Earth Search scene to map:', error)
        
        // Fallback: show scene info overlay
        this.addSceneInfoOverlay(scene.id)
        
        this.$store.commit('setAlert', {
          active: true,
          message: `Error loading satellite imagery: ${error.message}`,
          level: 'alert-danger',
          timeout: 5000
        })
      }
    },

    setupTileLoadingDetection() {
      console.log('🔍 Setting up tile loading detection...')
      
      // Simple approach: listen for when the map becomes idle (tiles loaded)
      this.map.on('idle', () => {
        console.log('🧹 Map idle event fired - tiles should be visible')
        
        // Check if our satellite layer exists and is loaded
        if (this.map.getLayer('earth-search-scene-layer') && this.map.isSourceLoaded('earth-search-scene')) {
          // Give a small delay to ensure tiles are actually rendered
          setTimeout(() => {
            console.log('✅ Tiles are now visible, hiding loading spinner')
            this.tileLoading = false
            this.$store.commit('setAlert', { active: false }) // Hide loading alert
          }, 500) // Reduced delay to 500ms
        }
      })
      
      // Also listen for tile loading errors
      this.map.on('error', (e) => {
        if (e.error && e.error.message && e.error.message.includes('tile')) {
          console.warn('⚠️ Tile loading error:', e.error.message)
          // Hide spinner on error after a short delay
          setTimeout(() => {
            console.log('⚠️ Hiding loading spinner due to tile loading error')
            this.tileLoading = false
          }, 2000)
        }
      })
      
      // Set a reasonable timeout as fallback
      setTimeout(() => {
        if (this.tileLoading) {
          console.log('⏰ Timeout reached, hiding loading spinner')
          this.tileLoading = false
        }
      }, 10000) // Reduced to 10 seconds
    },

    async testTiTilerHealth() {
      try {
        console.log('🔍 Testing TiTiler service health...')
        const response = await fetch(`${this.titilerConfig.baseUrl}/health`)
        if (response.ok) {
          console.log('✅ TiTiler service is healthy')
          this.$store.commit('setAlert', {
            active: true,
            message: '✅ TiTiler service is healthy',
            level: 'alert-success',
            timeout: 3000
          })
        } else {
          console.error('❌ TiTiler service health check failed:', response.status)
          this.$store.commit('setAlert', {
            active: true,
            message: `❌ TiTiler service health check failed: ${response.status}`,
            level: 'alert-danger',
            timeout: 5000
          })
        }
      } catch (error) {
        console.error('❌ Error testing TiTiler health:', error)
        this.$store.commit('setAlert', {
          active: true,
          message: `❌ Error testing TiTiler health: ${error.message}`,
          level: 'alert-danger',
          timeout: 5000
        })
      }
    },

    clearTileLoadingDetection() {
      this.map.off('sourcedata')
      this.map.off('idle')
      this.map.off('error')
      console.log('🧹 Tile loading detection listeners cleared.')
    },

    // Enhanced Dataset Creation Methods
    async saveCurrentSceneAsDataset() {
      if (!this.selectedBlock) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'Please select a farm block first',
          level: 'alert-warning',
          timeout: 3000
        })
        return
      }

      if (!this.satelliteLayerAdded) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'No satellite imagery loaded. Please browse and select a scene first.',
          level: 'alert-warning',
          timeout: 3000
        })
        return
      }

      if (!this.currentLoadedScene) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'No satellite scene found. Please browse and select a scene first.',
          level: 'alert-warning',
          timeout: 3000
        })
        return
      }

      // Show the enhanced dataset creation modal instead of direct processing
      this.showDatasetModal = true
    },

    closeDatasetModal() {
      this.showDatasetModal = false
    },

    async createMultipleDatasets(datasetConfig) {
      console.log('🚀 Creating multiple datasets with config:', datasetConfig)
      
      try {
        // Show processing alert
        this.$store.commit('setAlert', {
          active: true,
          message: `🔄 Processing ${datasetConfig.datasets.length} satellite datasets...`,
          level: 'alert-info',
          timeout: 0
        })

        const createdDatasets = []
        const errors = []

        // Process each dataset configuration
        for (const dataset of datasetConfig.datasets) {
          try {
            console.log(`📊 Processing dataset: ${dataset.name}`)
            
            // Prepare function data for this specific dataset
            const functionData = {
              scene: this.currentLoadedScene,
              block: this.selectedBlock,
              farmId: this.currentFarm.id,
              userId: this.$store.state.currentUser.uid,
              datasetName: dataset.name,
              datasetDescription: dataset.description,
              // Enhanced processing options
              processingOptions: {
                type: dataset.type,
                expression: dataset.expression, // TiTiler band math expression
                bands: dataset.bands, // Required bands for this index
                colormap: dataset.colormap, // Appropriate colormap for visualization
                useTiTiler: true // Flag to use TiTiler for index calculation
              }
            }

            console.log('🔧 Function data for ' + dataset.name + ':', functionData)
            console.log('🔍 Available scene assets:', Object.keys(this.currentLoadedScene.assets))
            console.log('🔍 Scene assets details:', this.currentLoadedScene.assets)
            
            // Debug: Show what asset should be selected for NDVI
            if (dataset.type === 'ndvi') {
              console.log('🔍 NDVI Asset Selection Debug:')
              console.log('  - Visual asset:', this.currentLoadedScene.assets.visual)
              console.log('  - Red asset:', this.currentLoadedScene.assets.red)
              console.log('  - NIR asset:', this.currentLoadedScene.assets.nir)
              console.log('  - NIR08 asset:', this.currentLoadedScene.assets.nir08)
            }

            // Call the cloud function for this dataset. processSatellite-
            // Dataset now requires a verified Firebase ID token (see
            // functions/authMiddleware.js) — the server uses the token's
            // uid as the authoritative `userId` regardless of what the
            // body carries, and confirms the user has Editor+ on the
            // farmId before doing the work.
            const functionUrl = 'https://us-central1-efficient-vineyard.cloudfunctions.net/processSatelliteDataset'
            const idToken = await fb.auth.currentUser.getIdToken()
            const response = await fetch(functionUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              body: JSON.stringify(functionData)
            })

            if (!response.ok) {
              const errorText = await response.text()
              throw new Error('HTTP ' + response.status + ': ' + errorText)
            }

            const result = await response.json()
            console.log('✅ Dataset ' + dataset.name + ' created:', result)

            if (result.dataset) {
              createdDatasets.push({
                name: dataset.name,
                id: result.datasetId,
                type: dataset.type
              })
            }

          } catch (error) {
            console.error('❌ Error creating dataset ' + dataset.name + ':', error)
            errors.push({
              name: dataset.name,
              error: error.message
            })
          }
        }

        // Clear processing alert
        this.$store.commit('setAlert', { active: false })

        // Show results
        if (createdDatasets.length > 0) {
          const successMessage = '✅ Successfully created ' + createdDatasets.length + ' dataset(s): ' + createdDatasets.map(d => d.name).join(', ')
          
          this.$store.commit('setAlert', {
            active: true,
            message: successMessage,
            level: 'alert-success',
            timeout: 10000
          })

          console.log('🎉 All successful datasets:', createdDatasets)
        }

        if (errors.length > 0) {
          const errorMessage = '⚠️ ' + errors.length + ' dataset(s) failed: ' + errors.map(e => e.name + ' (' + e.error + ')').join(', ')
          
          this.$store.commit('setAlert', {
            active: true,
            message: errorMessage,
            level: 'alert-warning',
            timeout: 8000
          })

          console.log('⚠️ Failed datasets:', errors)
        }

        // Close the modal
        this.closeDatasetModal()

      } catch (error) {
        console.error('❌ Error in createMultipleDatasets:', error)
        
        // Clear processing alert
        this.$store.commit('setAlert', { active: false })
        
        // Show error message
        this.$store.commit('setAlert', {
          active: true,
          message: '❌ Failed to create datasets: ' + error.message,
          level: 'alert-danger',
          timeout: 8000
        })
      }
    }
  }
}
</script>

<style scoped>
.map-container {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.map-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

#satelliteImageryMap {
  background-color: #f8f9fa;
}

/* NASA Acres branding */
.satellite-header {
  border-top: 3px solid #0B3D91;
  border-bottom: 1px solid #dee2e6;
  background: linear-gradient(180deg, #fafbff 0%, #fff 100%);
}
.nasa-acres-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  padding: 6px 10px;
  border-radius: 8px;
  transition: transform 0.15s ease, background-color 0.15s ease;
}
.nasa-acres-badge:hover {
  transform: translateY(-1px);
  background-color: rgba(11, 61, 145, 0.05);
  text-decoration: none;
}
.nasa-acres-logo-img { height: 56px; width: auto; display: block; }
.nasa-acres-caption {
  font-size: 0.72em;
  color: #0B3D91;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.nasa-acres-footer {
  border-top: 1px dashed #dee2e6;
  padding-top: 1rem;
}
</style>
